import React, { useState, useMemo } from 'react';
import { Edit3 } from 'lucide-react';

const PHASE_NAMES = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

const getPhaseOptions = (count: number): string[] => {
  return PHASE_NAMES.slice(0, count);
};

type Props = {
  input: string;
  setInput: (val: string) => void;
  chatInputRef: React.RefObject<any>;
  isConnected: boolean;
  isGenerating: boolean;
  isAuditMode: boolean;
  phaseCount?: number;
};

export const ChatShortcuts: React.FC<Props> = ({
  input, setInput, chatInputRef, isConnected, isGenerating, isAuditMode, phaseCount = 7
}) => {
  const [selectedPlanOption, setSelectedPlanOption] = useState<string>("=方案？=");

  const phaseOptions = useMemo(() => getPhaseOptions(phaseCount), [phaseCount]);

  const activeDirectActions = isAuditMode 
    ? ["无误", "审完", "逐条返回", "确认/满意", "忽略，重新生成", "返回新教案Part 1", "Part 2", "Part 3"] 
    : ["合适", "继续", "确认", "满意", "复盘前详细方案", "复盘后详细方案"];
  
  const activeTemplateAction = isAuditMode ? "确认但补充一条意见：" : "不满意";

  const handlePlanOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSelectedPlanOption(val);
    if (val !== "=方案？=") {
      setInput(input + `方案${val}\n`);
      chatInputRef.current?.focus();
      setTimeout(() => setSelectedPlanOption("=方案？="), 100);
    }
  };

  const handleSelectAppend = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value !== "-1") {
      const text = e.target.selectedOptions[0]?.textContent || '';
      setInput(input + text);
      chatInputRef.current?.focus();
      setTimeout(() => { e.target.value = "-1"; }, 100);
    }
  };

  const btnClass = `whitespace-nowrap px-3 py-1.5 bg-white border text-xs rounded-full transition-colors shadow-sm disabled:opacity-50 ${isAuditMode ? 'border-purple-200 text-purple-700 hover:bg-purple-50 hover:border-purple-300' : 'border-gray-200 text-gray-600 hover:bg-brand-50 hover:text-brand-600 hover:border-brand-200'}`;

  return (
    <div className="flex flex-col gap-2">
      {/* 快捷按钮排 */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <select value={selectedPlanOption} onChange={handlePlanOptionChange} disabled={!isConnected || isGenerating} className={btnClass}>
          {["=方案？=", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O"].map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        
        {activeDirectActions.filter(a => !['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'].includes(a)).map(action => (
          <button key={action} disabled={!isConnected || isGenerating} className={btnClass} onClick={() => { setInput(input + action + '\n'); chatInputRef.current?.focus(); }}>
            {action}
          </button>
        ))}
        
        <button disabled={!isConnected || isGenerating} className="whitespace-nowrap px-3 py-1.5 bg-white border border-red-200 text-red-600 text-xs rounded-full hover:bg-red-50 hover:text-red-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1" onClick={() => { setInput(input + activeTemplateAction + '\n'); chatInputRef.current?.focus(); }}>
          <Edit3 size={10}/> {isAuditMode ? '补充意见...' : '不满意...'}
        </button>
      </div>

      {/* 但仅生成 下拉框 */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <button disabled={!isConnected || isGenerating} onClick={() => setInput(input + '但仅生成')} className={`whitespace-nowrap px-3 py-1.5 text-xs rounded-full transition-all shadow-md disabled:opacity-50 ${isAuditMode ? 'bg-gradient-to-r from-pink-400 to-purple-500 text-white' : 'bg-gradient-to-r from-blue-400 to-green-400 text-white'}`}>
          但仅生成
        </button>
        <select onChange={handleSelectAppend} disabled={!isConnected || isGenerating} className={btnClass}>
          <option value="-1">=环节=</option>
          <option value="整体方案设计">整体方案设计</option>
          {phaseOptions.map(p => (
            <option key={p} value={`环节${p}`}>环节{p}</option>
          ))}
          <option value="完整详细教案">完整详细教案</option>
        </select>
        <select onChange={handleSelectAppend} disabled={!isConnected || isGenerating} className={btnClass}>
          <option value="-1">=方案=</option>
          <option value="正方3个备选方案">正方3个备选方案</option>
          <option value="反方3个备选方案">反方3个备选方案</option>
          <option value="选定方案详细设计">选定方案详细设计</option>
          <option value="复盘并优化以上新版方案详细设计">复盘并优化以上新版方案详细设计</option>
          <option value="第一部分">第一部分</option>
          <option value="第二部分">第二部分</option>
          <option value="第三部分">第三部分</option>
          <option value="第四部分">第四部分</option>
          <option value="第五部分">第五部分</option>
        </select>
      </div>
    </div>
  );
};